import React from 'react';
import PropTypes from 'prop-types';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Skeleton } from 'antd';
import { Smiley } from '@phosphor-icons/react';
import Typography from '../../components/Typography';
import COLORS from '../../util/constants/colors';

export const SENTIMENT_BUCKETS = [
  { label: 'Very negative', bucket: '0-2' },
  { label: 'Negative', bucket: '3-4' },
  { label: 'Neutral', bucket: '5' },
  { label: 'Positive', bucket: '6-7' },
  { label: 'Very positive', bucket: '8-10' },
];

const CHART_HEIGHT = 260;
const X_AXIS_HEIGHT = 52;
const CHART_MARGIN = { bottom: 16 };

export const sentimentBucketLabel = (bucket) => SENTIMENT_BUCKETS.find((row) => row.bucket === bucket)?.label || 'Sentiment';

export const formatSentimentBucketTooltip = (bucket) => `Score ${bucket}: ${sentimentBucketLabel(bucket)}`;

const SentimentAxisTick = ({
  x,
  y,
  payload,
}) => {
  const bucket = payload.value;

  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor="middle" fill={COLORS.TEXT_TERTIARY_LITE}>
        <tspan x={0} y={10} fontSize={12} fontWeight={700}>
          {bucket}
        </tspan>
        <tspan x={0} y={26} fontSize={11}>
          {sentimentBucketLabel(bucket)}
        </tspan>
      </text>
    </g>
  );
};

const MairaSentiment = ({ sentiment, loading, onBucketClick }) => {
  const distribution = sentiment?.distribution || [];

  const handleBarClick = (entry) => {
    const bucket = entry?.payload?.bucket ?? entry?.bucket;
    if (bucket) onBucketClick(bucket);
  };

  return (
    <div className="maira-panel maira-sentiment">
      <Typography variant="text" size="medium" bold>
        <Smiley color={COLORS.SUCCESS_HOVER_LITE} size={16} weight="bold" />
        &nbsp;
        Sentiment Histogram
      </Typography>
      {loading ? (
        <Skeleton active />
      ) : (
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <BarChart data={distribution} margin={CHART_MARGIN}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="bucket"
              interval={0}
              height={X_AXIS_HEIGHT}
              tick={<SentimentAxisTick />}
              tickLine={false}
            />
            <YAxis allowDecimals={false} />
            <Tooltip
              formatter={(value) => [value, 'Calls']}
              labelFormatter={formatSentimentBucketTooltip}
            />
            <Bar
              dataKey="count"
              fill={COLORS.SUCCESS_HOVER_LITE}
              radius={[4, 4, 0, 0]}
              cursor="pointer"
              onClick={handleBarClick}
            />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

SentimentAxisTick.propTypes = {
  x: PropTypes.number,
  y: PropTypes.number,
  payload: PropTypes.shape({
    value: PropTypes.string,
  }),
};

SentimentAxisTick.defaultProps = {
  x: 0,
  y: 0,
  payload: { value: '' },
};

MairaSentiment.propTypes = {
  sentiment: PropTypes.object,
  loading: PropTypes.bool,
  onBucketClick: PropTypes.func,
};

MairaSentiment.defaultProps = {
  sentiment: {},
  loading: false,
  onBucketClick: () => {},
};

export default MairaSentiment;
